import React, { useState, useEffect } from 'react';
import Layout from '../../Layout/Layout';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { 
  FaSave, FaSpinner, FaExclamationTriangle,
  FaMoneyBillWave, FaCreditCard, FaMobileAlt,
  FaUniversity, FaWallet, FaInfoCircle,
  FaCheckCircle, FaTimesCircle, FaChevronDown,
  FaPlane,
  FaMoneyBill
} from 'react-icons/fa';
import './CreatePaymentMethod.css';

// API Configuration
const API_BASE_URL = 'http://localhost:8000/api';

// SweetAlert2 Configuration
const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true
});

const CreateSavingsGoals = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    notes: '',
    goal_type: 'investment',
    target_amount: 0,
    current_amount: 0,
    target_date: '',
    monthly_contribution: 0,
    priority: 0,

  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [tokenExpired, setTokenExpired] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const goalTypes = [
    { 
      value: 'emergency', 
      label: 'Emergency Fund',
      icon: <FaWallet />,
    },
    { 
      value: 'vacation', 
      label: 'Vacation',
      icon: <FaPlane />,
    },
    { 
      value: 'purchase', 
      label: 'Major Purchase',
      icon: <FaMoneyBill />,
    },
    { 
      value: 'investment', 
      label: 'Investment',
      icon: <FaMoneyBillWave />,
    },
    { 
      value: 'utilities', 
      label: 'Utilities',
      icon: <FaUniversity />,
    }
  ];

  // Load user data on component mount
  useEffect(() => {
    const loadUserData = () => {
      try {
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
          const parsedUserData = JSON.parse(storedUserData);
          setUserData(parsedUserData);
          console.log('Loaded user data:', parsedUserData);
        } 
      } catch (error) {
        console.error('Error loading user data:', error);
        setTokenExpired(true);
        showErrorAlert('Error loading user data. Please log in again.');
      }
    };

    loadUserData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear API error when form is edited
    if (apiError) {
      setApiError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.goal_type) {
      newErrors.goal_type = 'Goal type is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showSuccessAlert = (message) => {
    Toast.fire({
      icon: 'success',
      title: message,
      iconHtml: '<i class="fas fa-check-circle"></i>',
      customClass: {
        popup: 'swal2-popup-custom',
        title: 'swal2-title-custom'
      }
    });
  };

  const showErrorAlert = (message) => {
    Toast.fire({
      icon: 'error',
      title: message,
      iconHtml: '<i class="fas fa-times-circle"></i>',
      customClass: {
        popup: 'swal2-popup-custom',
        title: 'swal2-title-custom'
      }
    });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;


    setIsSubmitting(true);
    setApiError('');

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found. Please log in.');
      }

      const payload = {
        name: formData.name.trim(),
        notes: formData.notes.trim(),
        goal_type: formData.goal_type,
        target_amount: formData.target_amount,
        monthly_contribution: formData.monthly_contribution,
        priority: formData.priority,
        target_date: formData.target_date,
      };

      console.log('Submitting payment method data:', payload);

      const response = await fetch(`${API_BASE_URL}/savings-goals/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.status === 401) {
        throw new Error('Session expired');
      }

      const responseData = await response.json();
      console.log(responseData);
      
      if (!response.ok) {
        throw new Error(
          responseData.message || 
          responseData.detail || 
          responseData.error || 
          'Failed to create saving goal.'
        );
        
      }

      console.log('Saving Goal created successfully:', responseData);
      
      // Show success message
      showSuccessAlert('Saving Goal created successfully!');
      
      // Navigate after a short delay
      setTimeout(() => {
        navigate('/savings-goals');
      }, 1500);

    } catch (error) {
      console.error('Error creating saving goal.', error);
      
      if (error.message.includes('400')) {
        showErrorAlert('Invalid data provided. Please check your input and try again.');
      } else if (error.message.includes('403')) {
        showErrorAlert('You do not have permission to create saving goal.');
      } else if (error.message.includes('404')) {
        showErrorAlert('The payment method endpoint was not found.');
      } else if (error.message.includes('500')) {
        showErrorAlert('Server error. Please try again later.');
      } else {
        showErrorAlert(error.message || 'An error occurred while creating Saving goal');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      goal_type: value
    }));
    setIsSelectOpen(false);
    if (errors.payment_type) {
      setErrors(prev => ({
        ...prev,
        goal_type: ''
      }));
    }
  };

  const selectedGoalType = goalTypes.find(type => type.value === formData.goal_type);

  
  return (
    <Layout>
      <div className="create-payment-method">
        <div className="page-header">
          <h2>Create Savings Goal</h2>
          <p>Add a Saving goal.</p>
        </div>

        <form onSubmit={handleSubmit} className="payment-method-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">
                <FaWallet /> Saving Goal
              </label>
              <input
                style={{width: 'auto'}}
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Buying a Car, Buying a Motorcycle"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && (
                <span className="error-message">
                  <FaTimesCircle /> {errors.name}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="payment_type">
                <FaCreditCard /> Goal Type
              </label>
              <div className="select-container">
                <div 
                  className={`select-field ${isSelectOpen ? 'open' : ''} ${errors.goal_type ? 'error' : ''}`}
                  onClick={() => setIsSelectOpen(!isSelectOpen)}
                >
                  <div className="select-selected">
                    {selectedGoalType && (
                      <>
                        <div className="option-icon">{selectedGoalType.icon}</div>
                        <div className="option-details">
                          <span className="option-label">{selectedGoalType.label}</span>
                        </div>
                      </>
                    )}
                  </div>
                  <FaChevronDown className={`select-arrow ${isSelectOpen ? 'open' : ''}`} />
                </div>
                
                {isSelectOpen && (
                  <div className="select-options">
                    {goalTypes.map(type => (
                      <div
                        key={type.value}
                        className={`select-option ${formData.goal_type === type.value ? 'selected' : ''}`}
                        onClick={() => handleSelectChange(type.value)}
                      >
                        <div className="option-icon">{type.icon}</div>
                        <div className="option-details">
                          <span className="option-label">{type.label}</span>
                          <span className="option-description">{type.description}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {errors.payment_type && (
                <span className="error-message">
                  <FaTimesCircle /> {errors.goal_type}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="name">
                <FaWallet /> Target Amount
              </label>
              <input
                style={{width: 'auto'}}
                type="number"
                id="target_amount"
                name="target_amount"
                value={formData.target_amount}
                onChange={handleChange}
                placeholder="200"
                className={errors.targe_amount ? 'error' : ''}
              />
              {errors.name && (
                <span className="error-message">
                  <FaTimesCircle /> {errors.targe_amount}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="name">
                <FaWallet /> Target Date
              </label>
              <input
                style={{width: 'auto'}}
                type="date"
                id="target_date"
                name="target_date"
                value={formData.target_date}
                onChange={handleChange}
                placeholder="80"
                className={errors.target_date ? 'error' : ''}
              />
              {errors.name && (
                <span className="error-message">
                  <FaTimesCircle /> {errors.target_date}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="name">
                <FaWallet /> Monthly Contribution
              </label>
              <input
                style={{width: 'auto'}}
                type="number"
                id="monthly_contribution"
                name="monthly_contribution"
                value={formData.monthly_contribution}
                onChange={handleChange}
                placeholder="80"
                className={errors.monthly_contribution ? 'error' : ''}
              />
              {errors.name && (
                <span className="error-message">
                  <FaTimesCircle /> {errors.monthly_contribution}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="name">
                <FaWallet /> Priority
              </label>
              <input
                style={{width: 'auto'}}
                type="number"
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                placeholder="1"
                className={errors.priority ? 'error' : ''}
              />
              {errors.name && (
                <span className="error-message">
                  <FaTimesCircle /> {errors.priority}
                </span>
              )}
            </div>

            <div className="form-group full-width">
              <label htmlFor="notes">
                <FaInfoCircle /> Notes
              </label>
              <textarea
                style={{width: 'auto'}}
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Add any additional details about this saving goal(optional)..."
                rows="3"
              />
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={() => navigate('/savings-goals')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="spinner" /> Saving...
                </>
              ) : (
                <>
                  <FaSave /> Save
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateSavingsGoals;
